import type { ReactNode } from 'react';

/** Import lifecycle status, shared by every importer store. */
export type ImporterStatus =
  | 'idle'
  | 'converting'
  | 'placing'
  | 'done'
  | 'error';

export interface ImporterProgress {
  completed: number;
  total: number;
}

/**
 * The structural slice of a plugin's Zustand store that the shared flow reads.
 * Each app's store is a superset of this (it adds its own settings + setters).
 */
export interface ImporterStoreState {
  isReady: boolean;
  connectionError: string | null;
  skewMessage: string | null;
  file: File | null;
  status: ImporterStatus;
  progress: ImporterProgress | null;
  message: string | null;
  result: { count: number; skipped: number } | null;
  notice: string | null;
  setFile: (file: File | null) => void;
  startImport: () => void;
  cancel: () => void;
  reset: () => void;
  retryConnection: () => void;
}

/** A Zustand-style selector hook over a store state `S`. */
export type StoreHook<S> = <U>(selector: (state: S) => U) => U;

/**
 * Compile-time assertion that a Zustand store satisfies the {@link
 * ImporterStoreState} contract that {@link ImporterFlow} drives. Wrap the
 * `create(...)` call so the store file itself enforces the shared shape (and
 * catches drift early, independent of the `<ImporterFlow>` call site):
 *
 *   export const useImportStore = conformsToImporterFlow(create<ImportState>(...))
 *
 * It's the identity function at runtime — the constraint does all the work.
 */
export function conformsToImporterFlow<T extends StoreHook<ImporterStoreState>>(
  store: T,
): T {
  return store;
}

/** Static, per-app configuration + the settings UI for the flow. */
export interface FlowConfig {
  /** Plugin name, used as the error-logging context (e.g. "lottie-to-figma"). */
  app: string;
  /** Plugin title shown in the header. */
  title: string;
  /** One-line subtitle under the title. */
  subtitle: string;
  /** `accept` attribute for the file picker (e.g. ".ai,.eps"). */
  accept: string;
  /** Dropzone title (e.g. "Drop a PDF to begin"). */
  dropHint: string;
  /** Dropzone subtitle under the title (e.g. "We'll turn it into a Figma design…"). */
  dropSubhint?: string;
  /** Brand text shown in the footer. */
  footerLabel: string;
  /** The app-specific settings controls (mode/scale/etc.). */
  settings: ReactNode;
  /** Primary action label. Defaults to "Import". */
  importLabel?: string;
  /** Noun for the placed units in the success summary. Defaults to "layer". */
  unitNoun?: string;
}
